import React, {useState, useEffect, useRef} from "react"
import moment from "moment"

interface JourSpecifiqueHoraire {
  heureDebut: string // format "H:mm" ou "HH:mm"
  heureFin: string    // format "H:mm" ou "HH:mm"
}

type JourKey = 'LUN' | 'MAR' | 'MER' | 'JEU' | 'VEN' | 'SAM' | 'DIM'

type JoursSpecifiques = Partial<Record<JourKey, JourSpecifiqueHoraire>>

interface tempsEcoule {
  date: string | any | null | undefined,
  dateMax?: string | any | null | undefined,
  dateReel?: string | any | null | undefined,
  increment?: boolean | null | undefined,
  onChange?: any | null | undefined,
  heureOuverture?: string, // format "H:mm" ou "HH:mm"
  heureFermeture?: string, // format "H:mm" ou "HH:mm"
  heurePauseDebut?: string, // format "H:mm" ou "HH:mm" — début de la pause déjeuner
  heurePauseFin?: string,   // format "H:mm" ou "HH:mm" — fin de la pause déjeuner
  joursSpecifiques?: JoursSpecifiques | null, // horaires spécifiques par jour, prioritaires sur l'horaire global
  affichageBadge?: boolean,
}

const DEFAULT_OPENING = "09:00"
const DEFAULT_CLOSING = "18:00"
const DEFAULT_PAUSE_DEBUT = "12:00"
const DEFAULT_PAUSE_FIN = "14:00"

// ---- Parsing des heures d'ouverture/fermeture/pause ----

interface HourMinute {
  hour: number
  minute: number
}

// Parse "9:20" ou "17:55" -> { hour: 9, minute: 20 }
const parseHourMinute = (value: string): HourMinute => {
  const [hourStr, minuteStr] = value.split(':')
  const hour = parseInt(hourStr, 10)
  const minute = parseInt(minuteStr, 10)
  return {
    hour: Number.isNaN(hour) ? 0 : hour,
    minute: Number.isNaN(minute) ? 0 : minute,
  }
}

const toDecimalHour = ({ hour, minute }: HourMinute): number => hour + minute / 60

// Positionne un moment sur une heure/minute donnée (secondes/ms à 0)
const setToHourMinute = (m: moment.Moment, hm: HourMinute): moment.Moment => {
  return m.hour(hm.hour).minute(hm.minute).second(0).millisecond(0)
}

// Indique si un horaire d'ouverture/fermeture est réellement défini.
// Convention : "00:00"/"00:00" signifie "pas de restriction horaire" -> on ignore le calcul.
const isHoraireDefini = (heureOuverture: string, heureFermeture: string): boolean => {
  const openingHm = parseHourMinute(heureOuverture)
  const closingHm = parseHourMinute(heureFermeture)

  const isOpeningMidnight = openingHm.hour === 0 && openingHm.minute === 0
  const isClosingMidnight = closingHm.hour === 0 && closingHm.minute === 0

  return !(isOpeningMidnight && isClosingMidnight)
}

// Indique si une pause déjeuner est réellement définie.
// Convention : "00:00"/"00:00" ou une plage nulle/inversée -> pas de pause.
const isPauseDefinie = (heurePauseDebut: string, heurePauseFin: string): boolean => {
  const debutHm = parseHourMinute(heurePauseDebut)
  const finHm = parseHourMinute(heurePauseFin)

  const isDebutMidnight = debutHm.hour === 0 && debutHm.minute === 0
  const isFinMidnight = finHm.hour === 0 && finHm.minute === 0

  if (isDebutMidnight && isFinMidnight) {
    return false
  }

  return toDecimalHour(finHm) > toDecimalHour(debutHm)
}

// ---- Jours spécifiques (horaires par jour de la semaine, prioritaires sur l'horaire global) ----

const JOUR_KEYS: JourKey[] = ['LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM', 'DIM']

// isoWeekday() : 1 = lundi ... 7 = dimanche
const getJourKey = (m: moment.Moment): JourKey => JOUR_KEYS[m.isoWeekday() - 1]

// Renvoie les horaires d'ouverture/fermeture effectifs pour un jour donné :
// l'override du jour spécifique s'il existe, sinon l'horaire global.
const getOpeningHoursForDay = (
  day: moment.Moment,
  heureOuverture: string,
  heureFermeture: string,
  joursSpecifiques?: JoursSpecifiques | null
): { openingHm: HourMinute; closingHm: HourMinute } => {
  const override = joursSpecifiques ? joursSpecifiques[getJourKey(day)] : undefined

  if (override) {
    return {
      openingHm: parseHourMinute(override.heureDebut),
      closingHm: parseHourMinute(override.heureFin),
    }
  }

  return {
    openingHm: parseHourMinute(heureOuverture),
    closingHm: parseHourMinute(heureFermeture),
  }
}

// ---- Jours fériés français ----

// Calcule le dimanche de Pâques pour une année donnée (algorithme de Meeus/Jones/Butcher)
const computeEasterSunday = (year: number): moment.Moment => {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1

  return moment({ year, month: month - 1, day })
}

// Cache par année pour éviter de recalculer Pâques à chaque appel (utile vu le setInterval à 1s)
const frenchHolidaysCache = new Map<number, moment.Moment[]>()

const getFrenchHolidays = (year: number): moment.Moment[] => {
  if (frenchHolidaysCache.has(year)) {
    return frenchHolidaysCache.get(year) as moment.Moment[]
  }

  const easter = computeEasterSunday(year)

  const holidays = [
    moment({ year, month: 0, day: 1 }),   // Jour de l'an
    easter.clone().add(1, 'day'),         // Lundi de Pâques
    moment({ year, month: 4, day: 1 }),   // Fête du Travail
    moment({ year, month: 4, day: 8 }),   // Victoire 1945
    easter.clone().add(39, 'day'),        // Ascension
    easter.clone().add(50, 'day'),        // Lundi de Pentecôte
    moment({ year, month: 6, day: 14 }),  // Fête nationale
    moment({ year, month: 7, day: 15 }),  // Assomption
    moment({ year, month: 10, day: 1 }),  // Toussaint
    moment({ year, month: 10, day: 11 }), // Armistice
    moment({ year, month: 11, day: 25 }), // Noël
  ]

  frenchHolidaysCache.set(year, holidays)
  return holidays
}

const isWeekend = (m: moment.Moment): boolean => {
  const isoWeekday = m.isoWeekday()
  return isoWeekday === 6 || isoWeekday === 7
}

const isFrenchHoliday = (m: moment.Moment): boolean => {
  return getFrenchHolidays(m.year()).some(h => h.isSame(m, 'day'))
}

// Un jour est "ouvré" si :
// - ce n'est pas un jour férié (priorité absolue, même sur un override)
// - ET soit un jour spécifique est explicitement défini pour ce jour (il devient ouvré,
//   même un samedi/dimanche, avec ses propres horaires)
// - OU, à défaut, ce n'est pas un week-end (règle globale par défaut)
const isBusinessDay = (
  m: moment.Moment,
  joursSpecifiques?: JoursSpecifiques | null
): boolean => {
  if (isFrenchHoliday(m)) {
    return false
  }

  const override = joursSpecifiques ? joursSpecifiques[getJourKey(m)] : undefined
  if (override) {
    return true
  }

  return !isWeekend(m)
}

// Renvoie l'heure de fermeture effective (globale ou spécifique au jour) du dernier
// jour ouvré strictement antérieur à "from"
const getPreviousBusinessDayClosing = (
  from: moment.Moment,
  heureOuverture: string,
  heureFermeture: string,
  joursSpecifiques?: JoursSpecifiques | null
): moment.Moment => {
  const d = from.clone().subtract(1, 'day')
  while (!isBusinessDay(d, joursSpecifiques)) {
    d.subtract(1, 'day')
  }
  const { closingHm } = getOpeningHoursForDay(d, heureOuverture, heureFermeture, joursSpecifiques)
  return setToHourMinute(d, closingHm)
}

// Indique si on est actuellement en dehors des heures d'ouverture
// (jour non ouvré, jour férié, avant l'ouverture, après la fermeture, ou pendant la pause déjeuner)
// — en tenant compte des horaires spécifiques par jour s'ils existent
const isOutsideOpeningHours = (
  heureOuverture: string,
  heureFermeture: string,
  heurePauseDebut: string,
  heurePauseFin: string,
  joursSpecifiques?: JoursSpecifiques | null
): boolean => {
  if (!isHoraireDefini(heureOuverture, heureFermeture)) {
    return false
  }

  const now = moment()

  if (!isBusinessDay(now, joursSpecifiques)) {
    return true
  }

  const { openingHm, closingHm } = getOpeningHoursForDay(now, heureOuverture, heureFermeture, joursSpecifiques)
  const hourDecimal = now.hour() + now.minute() / 60

  if (hourDecimal < toDecimalHour(openingHm) || hourDecimal >= toDecimalHour(closingHm)) {
    return true
  }

  if (isPauseDefinie(heurePauseDebut, heurePauseFin)) {
    const pauseDebutHm = parseHourMinute(heurePauseDebut)
    const pauseFinHm = parseHourMinute(heurePauseFin)
    const pauseDebutDecimal = toDecimalHour(pauseDebutHm)
    const pauseFinDecimal = toDecimalHour(pauseFinHm)

    if (hourDecimal >= pauseDebutDecimal && hourDecimal < pauseFinDecimal) {
      return true
    }
  }

  return false
}

// Renvoie l'heure "effective" à utiliser pour la comparaison d'urgence par rapport à dateMax,
// en tenant compte des horaires spécifiques par jour s'ils existent
const getEffectiveNow = (
  heureOuverture: string,
  heureFermeture: string,
  heurePauseDebut: string,
  heurePauseFin: string,
  joursSpecifiques?: JoursSpecifiques | null
): moment.Moment => {
  const now = moment()

  if (!isHoraireDefini(heureOuverture, heureFermeture)) {
    return now
  }

  if (!isBusinessDay(now, joursSpecifiques)) {
    return getPreviousBusinessDayClosing(now, heureOuverture, heureFermeture, joursSpecifiques)
  }

  const { openingHm, closingHm } = getOpeningHoursForDay(now, heureOuverture, heureFermeture, joursSpecifiques)
  const hourDecimal = now.hour() + now.minute() / 60

  if (hourDecimal < toDecimalHour(openingHm)) {
    return getPreviousBusinessDayClosing(now, heureOuverture, heureFermeture, joursSpecifiques)
  }

  if (hourDecimal >= toDecimalHour(closingHm)) {
    return setToHourMinute(now.clone(), closingHm)
  }

  if (isPauseDefinie(heurePauseDebut, heurePauseFin)) {
    const pauseDebutHm = parseHourMinute(heurePauseDebut)
    const pauseFinHm = parseHourMinute(heurePauseFin)
    const pauseDebutDecimal = toDecimalHour(pauseDebutHm)
    const pauseFinDecimal = toDecimalHour(pauseFinHm)

    if (hourDecimal >= pauseDebutDecimal && hourDecimal < pauseFinDecimal) {
      return setToHourMinute(now.clone(), pauseDebutHm)
    }
  }

  return now
}

// Le chrono est "en pause" quand il n'y a pas de dateReel, qu'un horaire est réellement
// défini, et qu'on est actuellement hors des heures d'ouverture effectives du jour
const isTimerPaused = (
  dateReel: any,
  heureOuverture: string,
  heureFermeture: string,
  heurePauseDebut: string,
  heurePauseFin: string,
  joursSpecifiques?: JoursSpecifiques | null
): boolean => {
  if (dateReel !== null && dateReel !== undefined) {
    return false
  }
  if (!isHoraireDefini(heureOuverture, heureFermeture)) {
    return false
  }
  return isOutsideOpeningHours(heureOuverture, heureFermeture, heurePauseDebut, heurePauseFin, joursSpecifiques)
}

// ---- Calcul du temps réellement écoulé/restant en heures ouvrées ----

// Renvoie les segments ouvrés d'un jour donné (horaire spécifique ou global),
// en excluant la pause déjeuner si elle est définie.
const getDayBusinessSegments = (
  day: moment.Moment,
  heureOuverture: string,
  heureFermeture: string,
  pauseDebutHm: HourMinute | null,
  pauseFinHm: HourMinute | null,
  joursSpecifiques?: JoursSpecifiques | null
): Array<{ start: moment.Moment; end: moment.Moment }> => {
  const { openingHm, closingHm } = getOpeningHoursForDay(day, heureOuverture, heureFermeture, joursSpecifiques)
  const open = setToHourMinute(day.clone(), openingHm)
  const close = setToHourMinute(day.clone(), closingHm)

  // Horaire nul ou inversé pour ce jour (ex: override "00:00"/"00:00") -> jour sans contribution
  if (!close.isAfter(open)) {
    return []
  }

  if (!pauseDebutHm || !pauseFinHm) {
    return [{ start: open, end: close }]
  }

  const pauseStart = setToHourMinute(day.clone(), pauseDebutHm)
  const pauseEnd = setToHourMinute(day.clone(), pauseFinHm)

  const clampedPauseStart = moment.max(open, moment.min(pauseStart, close))
  const clampedPauseEnd = moment.max(open, moment.min(pauseEnd, close))

  if (!clampedPauseEnd.isAfter(clampedPauseStart)) {
    return [{ start: open, end: close }]
  }

  const segments: Array<{ start: moment.Moment; end: moment.Moment }> = []
  if (clampedPauseStart.isAfter(open)) {
    segments.push({ start: open, end: clampedPauseStart })
  }
  if (close.isAfter(clampedPauseEnd)) {
    segments.push({ start: clampedPauseEnd, end: close })
  }
  return segments
}

// Additionne uniquement les heures ouvrées (jours ouvrés, horaire du jour — spécifique
// ou global —, hors pause déjeuner) comprises entre "start" et "end"
const computeBusinessDiffMs = (
  start: moment.Moment,
  end: moment.Moment,
  heureOuverture: string,
  heureFermeture: string,
  pauseDebutHm: HourMinute | null,
  pauseFinHm: HourMinute | null,
  joursSpecifiques?: JoursSpecifiques | null
): number => {
  if (!end.isAfter(start)) {
    return 0
  }

  let total = 0
  const cursorDay = start.clone().startOf('day')
  const lastDay = end.clone().startOf('day')

  while (cursorDay.isSameOrBefore(lastDay, 'day')) {
    if (isBusinessDay(cursorDay, joursSpecifiques)) {
      const segments = getDayBusinessSegments(cursorDay, heureOuverture, heureFermeture, pauseDebutHm, pauseFinHm, joursSpecifiques)
      for (const segment of segments) {
        const segStart = moment.max(segment.start, start)
        const segEnd = moment.min(segment.end, end)
        if (segEnd.isAfter(segStart)) {
          total += segEnd.diff(segStart)
        }
      }
    }
    cursorDay.add(1, 'day')
  }

  return total
}

// méthode qui calcule le temps formatté "HH:mm" pour un état donné
// (utilisée à la fois pour l'initialisation immédiate et pour les mises à jour)
const computeTemps = (
  date: any,
  dateMax: any,
  dateReel: any,
  increment: boolean | null,
  heureOuverture: string,
  heureFermeture: string,
  heurePauseDebut: string,
  heurePauseFin: string,
  joursSpecifiques?: JoursSpecifiques | null
): string => {
  if (date === null || date === undefined || dateMax === null || dateMax === undefined || increment === null) {
    return "--:--:--"
  }

  const dateValue = moment(date)
  const datemaxValue = moment(dateMax)

  let diff: number
  if (dateReel !== null && dateReel !== undefined) {
    // chronomètre arrêté : durée figée entre date et dateReel (calcul brut, non ajusté aux heures ouvrées)
    diff = moment(dateReel).diff(dateValue)
  } else if (!isHoraireDefini(heureOuverture, heureFermeture)) {
    // pas d'horaire configuré : calcul classique en continu
    diff = increment
      ? moment().diff(dateValue)
      : datemaxValue.diff(moment())
  } else {
    // horaire configuré : on ne compte que les heures ouvrées réellement écoulées,
    // en tenant compte des jours spécifiques et de la pause déjeuner
    const pauseActive = isPauseDefinie(heurePauseDebut, heurePauseFin)
    const pauseDebutHm = pauseActive ? parseHourMinute(heurePauseDebut) : null
    const pauseFinHm = pauseActive ? parseHourMinute(heurePauseFin) : null
    const now = moment()
    diff = increment
      ? computeBusinessDiffMs(dateValue, now, heureOuverture, heureFermeture, pauseDebutHm, pauseFinHm, joursSpecifiques)
      : computeBusinessDiffMs(now, datemaxValue, heureOuverture, heureFermeture, pauseDebutHm, pauseFinHm, joursSpecifiques)
  }

  let duration = moment.duration(Math.max(0, diff))

  let totalSeconds = Math.floor(duration.asSeconds())
  let hours = Math.floor(totalSeconds / 3600)
  totalSeconds %= 3600
  let minutes = Math.floor(totalSeconds / 60)
  let seconds = totalSeconds % 60

  const hoursStr = String(hours).padStart(2, '0')
  const minutesStr = String(minutes).padStart(2, '0')
  const secondsStr = String(seconds).padStart(2, '0')

  return `${hoursStr}:${minutesStr}`
}

/**
 * Affiche le temps ecoulé
 *
 * Ce composant affiche le temps écoulé entre date et dateMax, et continue de s'incrémenter jusqu'à ce que dateReel soit définie.
 * Il peut aussi afficher le temps restant entre date et dateMax, en se décrémentant jusqu'à zéro, sans nécessiter dateReel.
 *
 * - date : La date et l'heure (string ou moment) de début
 * - dateMax : La date et l'heure (string ou moment) à ne pas dépasser par rapport à maintenant
 * - dateReel : La date et l'heure (string ou moment) precise qui marque l'arrêt du chronomètre
 * - increment : Un booléen qui défini quel action faire, incrémenter continuellement ou décrémenter à zéro
 * - onChange : Un callback qui permet de recupérer le temps
 * - heureOuverture : Heure d'ouverture globale au format "H:mm", par défaut "09:00"
 * - heureFermeture : Heure de fermeture globale au format "H:mm", par défaut "18:00"
 * - heurePauseDebut : Début de la pause déjeuner au format "H:mm", par défaut "12:00"
 * - heurePauseFin : Fin de la pause déjeuner au format "H:mm", par défaut "14:00"
 * - joursSpecifiques : Horaires par jour de la semaine (LUN, MAR, MER, JEU, VEN, SAM, DIM),
 *   prioritaires sur l'horaire global pour le(s) jour(s) concerné(s). Ex:
 *   { LUN: { heureDebut: '09:00', heureFin: '19:00' }, MAR: { heureDebut: '11:00', heureFin: '18:00' } }
 * - affichageBadge : Si true, affiche le temps sous forme de badge, par défaut false
 *
 * Le chronomètre ne comptabilise que les heures réellement ouvrées (hors nuits,
 * jours non ouvrés, jours fériés français et pause déjeuner), tant que dateReel n'est pas défini.
 * Une icône pause (bi-pause-fill) remplace le sablier, et le texte "Pause" remplace "00:00",
 * quand le chrono est figé sans rien accumulé.
 * */
const TempsEcoule = (
  {
    date,
    dateMax = null,
    dateReel = null,
    increment = true,
    onChange = null,
    heureOuverture = DEFAULT_OPENING,
    heureFermeture = DEFAULT_CLOSING,
    heurePauseDebut = DEFAULT_PAUSE_DEBUT,
    heurePauseFin = DEFAULT_PAUSE_FIN,
    joursSpecifiques = null,
    affichageBadge = false,
  }: tempsEcoule
) => {
  // initialisation "lazy" : on calcule directement la vraie valeur au montage
  // au lieu de partir sur "--:--:--" et d'attendre le premier tick
  const [temps, setTemps] = useState<string>(() =>
    computeTemps(date, dateMax, dateReel, increment, heureOuverture, heureFermeture, heurePauseDebut, heurePauseFin, joursSpecifiques)
  )

  // état de pause suivi séparément de "temps" : une transition true<->false doit
  // déclencher un re-render même si le texte affiché (précision minute) n'a pas changé,
  // sinon React 18 ignore le setState "temps" (valeur identique) et le composant ne se
  // re-render jamais pendant les longues périodes où l'affichage reste figé
  const [paused, setPaused] = useState<boolean>(() =>
    isTimerPaused(dateReel, heureOuverture, heureFermeture, heurePauseDebut, heurePauseFin, joursSpecifiques)
  )

  // ref pour garder la dernière valeur de onChange sans le mettre dans les deps de l'effet
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  useEffect(() => {
    if (affichageBadge) return

    // Pas de date -> on affiche le placeholder, mais seulement si nécessaire
    if (date === null || date === undefined || dateMax === null || dateMax === undefined) {
      setTemps("--:--:--")
      setPaused(false)
      return
    }

    // calcule et affiche immédiatement la valeur courante (pas d'attente du 1er tick)
    const applyTemps = () => {
      const value = computeTemps(date, dateMax, dateReel, increment, heureOuverture, heureFermeture, heurePauseDebut, heurePauseFin, joursSpecifiques)
      setTemps(value)

      // recalculé et poussé à chaque tick, indépendamment de "value" : c'est ce qui
      // garantit que le passage en/hors pause est détecté en temps réel
      const nextPaused = isTimerPaused(dateReel, heureOuverture, heureFermeture, heurePauseDebut, heurePauseFin, joursSpecifiques)
      setPaused(nextPaused)

      if (onChangeRef.current) onChangeRef.current(value)
      return value
    }

    const initial = applyTemps()

    // Si dateReel est définie, le chrono est figé : pas besoin d'interval
    if (dateReel !== null && dateReel !== undefined) {
      return
    }

    // Si on décrémente et qu'on est déjà à 0, pas besoin de lancer l'interval
    if (!increment && initial === "00:00") {
      return
    }

    const interval = setInterval(() => {
      const value = applyTemps()
      if (!increment && value === "00:00") {
        clearInterval(interval)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [date, dateMax, dateReel, increment, heureOuverture, heureFermeture, heurePauseDebut, heurePauseFin, joursSpecifiques])

  // méthode qui ajuste le code couleur
  const colorCode = () => {
    const dateValueMoment = dateReel
      ? moment(dateReel)
      : getEffectiveNow(heureOuverture, heureFermeture, heurePauseDebut, heurePauseFin, joursSpecifiques)
    const dateMaxMoment = moment(dateMax)

    if (date !== null && dateMax !== null) {
      if (dateValueMoment < dateMaxMoment) {
        const dateProcheMarge = dateMaxMoment.clone().subtract(30, "minutes")
        if (dateValueMoment < dateProcheMarge) {
          return 'text-primary'
        } else {
          return 'text-warning'
        }
      } else {
        return 'text-danger'
      }
    }
  }

  // méthode qui affiche soit l'icône de pause (hors horaires/jour spécifique/pause déjeuner),
  // soit un sablier selon le code couleur du délai de traitement — jamais les deux côte à côte
  const renderNiveauDelaiTraitement = (paused: boolean) => {
    const dateValueMoment = dateReel
      ? moment(dateReel)
      : getEffectiveNow(heureOuverture, heureFermeture, heurePauseDebut, heurePauseFin, joursSpecifiques)
    const dateMaxMoment = moment(dateMax)

    if (date === null || dateMax === null) {
      return null
    }

    if (dateValueMoment < dateMaxMoment) {
      const dateProcheMarge = dateMaxMoment.clone().subtract(30, "minutes")
      const color = dateValueMoment < dateProcheMarge ? 'text-primary' : 'text-warning'

      if (paused) {
        return <i className={`bi bi-pause-fill fs-5 me-2 text-hover-none ${color} align-middle`} title="En pause (hors horaires d'ouverture)"></i>
      }
      return dateValueMoment < dateProcheMarge
        ? <i className='bi bi-hourglass-top fs-5 me-2 text-hover-none text-primary align-middle'></i>
        : <i className='bi bi-hourglass-split fs-5 me-2 text-hover-none text-warning align-middle'></i>
    } else {
      if (paused) {
        return <i className='bi bi-pause-fill fs-5 me-2 text-hover-none text-danger align-middle' title="En pause (hors horaires d'ouverture)"></i>
      }
      return <i className='bi bi-hourglass-bottom fs-5 me-2 text-hover-none text-danger align-middle'></i>
    }
  }

  const renderBadge = () => {
    if (!date || !dateMax || temps === "--:--:--") {
      return <span className='badge badge-secondary d-block text-center fw-bolder'>--h --</span>
    }
    const color = colorCode()
    return (
      <span title={moment(dateMax).format('DD/MM/YYYY à HH:mm')} className={`badge badge-light-${color?.replace('text-', '')} ${color} d-block text-center fw-bolder`}>
        {temps.replace(':', 'h ')}
      </span>
    )
  }

  if (affichageBadge) {
    return renderBadge()
  }

  return (
    <>
      {(date !== null) ? (
        <>
          {increment ? renderNiveauDelaiTraitement(paused) : null}
          {(date && dateMax)
            ? (paused && temps === "00:00")
              ? (
                <span title={moment(dateMax).format('DD/MM/YYYY à HH:mm')} className={`min-w-75px mw-100px d-inline-block ${increment ? 'text-start' : 'text-center'} ${colorCode()}`}>
                  Pause
                </span>
              ) : (increment && temps === "00:00" && !paused)
                ? (
                  <span title={moment(dateMax).format('DD/MM/YYYY à HH:mm')} className={`spinner-border spinner-border-sm align-middle ${colorCode()} ms-2 mb-1`}></span>
                ) : (
                  <span title={moment(dateMax).format('DD/MM/YYYY à HH:mm')} className={`min-w-75px mw-100px d-inline-block ${increment ? 'text-start' : 'text-center'} ${colorCode()}`}>
                    {temps}
                  </span>
                )
            : null
          }
        </>
      ) : null}
    </>
  )
}

// Comparaison personnalisée : évite un re-render/remount inutile quand le parent
// recrée un nouveau tableau/objet de props à chaque rechargement de liste,
// alors que les valeurs "métier" (date, dateMax, dateReel, increment) sont identiques
const areEqual = (prev: tempsEcoule, next: tempsEcoule) => {
  const sameOrEqual = (a: any, b: any) => {
    if (a === b) return true
    if (a === null || a === undefined || b === null || b === undefined) return a === b
    return moment(a).isSame(moment(b))
  }

  const sameJoursSpecifiques = (a?: JoursSpecifiques | null, b?: JoursSpecifiques | null) => {
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
  }

  return (
    sameOrEqual(prev.date, next.date) &&
    sameOrEqual(prev.dateMax, next.dateMax) &&
    sameOrEqual(prev.dateReel, next.dateReel) &&
    prev.increment === next.increment &&
    prev.heureOuverture === next.heureOuverture &&
    prev.heureFermeture === next.heureFermeture &&
    prev.heurePauseDebut === next.heurePauseDebut &&
    prev.heurePauseFin === next.heurePauseFin &&
    sameJoursSpecifiques(prev.joursSpecifiques, next.joursSpecifiques)
  )
}
export default TempsEcoule